import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment";

const wrapAngle = (angle) => THREE.MathUtils.euclideanModulo(angle + Math.PI, Math.PI * 2) - Math.PI;
const deltaToTarget = (current, target) => wrapAngle(target - current);
const dampAngle = (current, target, lambda, delta) => {
  const diff = deltaToTarget(current, target);
  if (Math.abs(diff) < 1e-5) return target;
  const step = THREE.MathUtils.damp(0, diff, lambda, delta);
  return current + step;
};

const SPIN_SPEED = 1.2; // radians per second
const PRERENDER_USER_AGENT_TOKEN = "KyrosPrerender";

export default function RotatingModel({
  width = 72,
  height = 72,
  fit = 1.8,
  className = "",
  style = {},
  offsetTop = 8,
  onActivate,
  isChatOpen = false
}) {
  const mountRef = useRef(null);
  const canvasRef = useRef(null);
  const modelRef = useRef(null);
  const activateRef = useRef(onActivate);
  const chatOpenRef = useRef(isChatOpen);
  const [canInitialize, setCanInitialize] = useState(false);
  const animationStateRef = useRef({
    mode: "spin",
    pivotSpin: null,
    pivotTilt: null,
    clock: null,
    raf: null,
    spinAngle: 0
  });

  useEffect(() => {
    activateRef.current = onActivate;
  }, [onActivate]);

  useEffect(() => {
    chatOpenRef.current = isChatOpen;
    const state = animationStateRef.current;
    if (!state.pivotSpin) return;

    if (isChatOpen) {
      if (state.mode === "spin") {
        state.mode = "toFront";
      }
    } else if (state.mode !== "spin") {
      state.mode = "spin";
    }
  }, [isChatOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (
      typeof navigator !== "undefined" &&
      navigator.userAgent.includes(PRERENDER_USER_AGENT_TOKEN)
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setCanInitialize(true), 150);
    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!canInitialize) return undefined;
    if (
      typeof navigator !== "undefined" &&
      navigator.userAgent.includes(PRERENDER_USER_AGENT_TOKEN)
    ) {
      return undefined;
    }

    const mount = mountRef.current;
    const canvas = canvasRef.current;
    if (!mount || !canvas) return undefined;

    const previousCursor = mount.style.cursor;
    const previousCanvasCursor = canvas.style.cursor;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.01, 1000);

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(width, height, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.45;
    renderer.domElement.style.cursor = "pointer";
    mount.style.cursor = "pointer";

    const pmrem = new THREE.PMREMGenerator(renderer);
    const envTex = pmrem.fromScene(new RoomEnvironment(renderer), 0.04).texture;
    scene.environment = envTex;

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dir = new THREE.DirectionalLight(0xffffff, 1.2);
    dir.position.set(3, 4, 5);
    scene.add(dir);

    const state = animationStateRef.current;
    state.mode = "spin";
    state.pivotSpin = null;
    state.pivotTilt = null;
    state.clock = new THREE.Clock();
    state.clock.start();
    state.raf = null;
    state.spinAngle = 0;

    const loader = new GLTFLoader();
    loader.load(`${process.env.PUBLIC_URL}/models/model.glb`, (gltf) => {
      const model = gltf.scene;
      model.traverse((o) => {
        if (!o.isMesh) return;
        const materials = Array.isArray(o.material) ? o.material : [o.material];
        materials.forEach((m) => {
          if (!m) return;
          if ("envMapIntensity" in m) m.envMapIntensity = 1.3;
          if ("roughness" in m && m.roughness > 0.6) m.roughness = 0.45;
          m.needsUpdate = true;
        });
      });

      const pivotTilt = new THREE.Group();
      const pivotSpin = new THREE.Group();
      pivotSpin.add(model);
      pivotTilt.add(pivotSpin);
      scene.add(pivotTilt);

      modelRef.current = model;
      state.pivotSpin = pivotSpin;
      state.pivotTilt = pivotTilt;

      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);
      model.position.sub(center);
      const maxDim = Math.max(size.x, size.y, size.z);
      const dist = (maxDim / (2 * Math.tan((camera.fov * Math.PI) / 360))) * fit;
      camera.position.set(0, 0, dist);
      camera.near = maxDim / 100;
      camera.far = maxDim * 100;
      camera.updateProjectionMatrix();

      const animate = () => {
        const currentState = animationStateRef.current;
        const pivot = currentState.pivotSpin;
        currentState.raf = requestAnimationFrame(animate);

        if (pivot) {
          const rawDelta = currentState.clock ? currentState.clock.getDelta() : 0.016;
          const delta = Math.min(rawDelta, 0.05);
          const mode = currentState.mode;

          if (mode === "spin") {
            const baseAngle =
              typeof currentState.spinAngle === "number" ? currentState.spinAngle : pivot.rotation.y;
            const nextAngle = baseAngle + delta * SPIN_SPEED;
            currentState.spinAngle = nextAngle;
            pivot.rotation.y = wrapAngle(nextAngle);
          } else if (mode === "toFront") {
            pivot.rotation.y = dampAngle(pivot.rotation.y, 0, 6, delta);
            currentState.spinAngle = pivot.rotation.y;
            const closeEnough = Math.abs(deltaToTarget(pivot.rotation.y, 0)) < 0.01;
            if (closeEnough) {
              pivot.rotation.y = 0;
              currentState.spinAngle = 0;
              currentState.mode = chatOpenRef.current ? "chat" : "spin";
            }
          } else if (mode === "chat") {
            pivot.rotation.y = dampAngle(pivot.rotation.y, 0, 10, delta);
            currentState.spinAngle = pivot.rotation.y;
          } else {
            currentState.spinAngle = pivot.rotation.y;
          }
        }

        renderer.render(scene, camera);
      };
      animate();

      const handleClick = () => {
        const currentState = animationStateRef.current;
        if (!currentState.pivotSpin) return;
        if (currentState.mode === "toFront" || currentState.mode === "chat") return;
        currentState.mode = "toFront";
        if (typeof activateRef.current === "function") activateRef.current();
      };

      mount.addEventListener("click", handleClick);

      modelRef.current.__cleanup = () => {
        mount.removeEventListener("click", handleClick);
      };
    });

    return () => {
      const stateRefSnapshot = state;
      if (stateRefSnapshot.raf) {
        cancelAnimationFrame(stateRefSnapshot.raf);
        stateRefSnapshot.raf = null;
      }
      stateRefSnapshot.clock?.stop();
      stateRefSnapshot.pivotSpin = null;
      stateRefSnapshot.pivotTilt = null;
      stateRefSnapshot.spinAngle = 0;

      const currentModel = modelRef.current;
      currentModel?.__cleanup?.();
      modelRef.current = null;

      mount.style.cursor = previousCursor;
      canvas.style.cursor = previousCanvasCursor;
      envTex.dispose();
      pmrem.dispose();
      renderer.dispose();
    };
  }, [canInitialize, width, height, fit]);

  return (
    <span
      ref={mountRef}
      className={`rotating-model ${className}`}
      style={{ width, height, display: "inline-block", marginTop: offsetTop, ...style }}
    >
      <canvas
        ref={canvasRef}
        className="rotating-model__canvas"
        aria-hidden="true"
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </span>
  );
}
